import { motion } from 'framer-motion';
import { CalendarDays, Play } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { trackCTA } from '../utils/analytics';

const tourSections = [
  {
    title: 'Dashboard',
    body: 'Review total sales, outstanding invoices, inventory count, low-stock alerts, customer count, and GST collected for the month.',
  },
  {
    title: 'Billing',
    body: 'Create invoices, quotations, proforma invoices, cash memos, delivery challans, credit notes, and debit notes with GST calculations built into line items.',
  },
  {
    title: 'Compliance',
    body: 'Manage e-invoice IRNs, e-way bills, GSTR views, CGST, SGST, IGST, profit and loss, and top customer reports from one place.',
  },
];

const Demo = () => (
  <>
    <SEOHead
      title="Demo"
      description="Tour the Varikanakku billbook experience across dashboard metrics, GST billing, parties, items, ledgers, e-invoices, e-way bills, and reports."
      url="https://varikanakku.com/demo"
    />

    <div className="border-b border-slate-200 bg-white">
      <div className="container-custom py-16 lg:py-20">
        <div className="mx-auto max-w-3xl text-center">
          <p className="eyebrow">Product tour</p>
          <h1 className="section-title mt-2">See the workflows Varikanakku already supports</h1>
          <p className="mt-4 text-lg text-surface-body">
            The app is built around the daily work of billing, collections, stock
            tracking, ledger entries, and GST compliance.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-surface-page py-16">
      <div className="container-custom">
        <div className="mx-auto max-w-5xl space-y-12">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg border border-slate-200 bg-white p-4 shadow-card"
          >
            <div className="grid overflow-hidden rounded-lg border border-slate-200 lg:grid-cols-[220px_1fr]">
              <aside className="bg-gradient-to-b from-cyan-700 via-sky-700 to-violet-700 p-5 text-white">
                <div className="flex items-center gap-2">
                  <Play size={18} className="text-primary-300" aria-hidden />
                  <span className="text-sm font-semibold">Billbook</span>
                </div>
                <nav className="mt-6 space-y-2 text-sm">
                  {['Dashboard', 'Invoices', 'Parties', 'Items', 'GST reports'].map(item => (
                    <div
                      key={item}
                      className={`rounded-lg px-3 py-2 ${item === 'Dashboard' ? 'bg-white/20 text-cyan-50' : 'text-cyan-50/85'}`}
                    >
                      {item}
                    </div>
                  ))}
                </nav>
              </aside>
              <div className="p-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-blue-900">Dashboard</h2>
                    <p className="text-sm text-cyan-700">Overview of your business performance</p>
                  </div>
                  <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
                    Active business
                  </span>
                </div>
                <div className="mt-5 grid gap-3 md:grid-cols-4">
                  {[
                    ['Total sales', 'INR 8.4L'],
                    ['Outstanding', 'INR 1.2L'],
                    ['Inventory items', '328'],
                    ['Customers', '142'],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-lg bg-slate-50 p-4">
                      <p className="text-xs font-medium text-cyan-700">{label}</p>
                      <p className="mt-2 text-2xl font-bold text-blue-900">{value}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-5 grid gap-3 md:grid-cols-3">
                  {['Create invoice', 'Record payment', 'View GST reports'].map(action => (
                    <div key={action} className="rounded-lg border border-cyan-100 p-4 text-sm font-semibold text-blue-800">
                      {action}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-3">
            {tourSections.map(({ title, body }) => (
              <div key={title} className="rounded-lg border border-slate-200 bg-white p-6 shadow-card">
                <h2 className="text-lg font-semibold text-surface-ink">{title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-surface-body">{body}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col items-center gap-6 sm:flex-row sm:justify-center">
            <a
              href="https://app.varikanakku.com/signup"
              rel="noopener noreferrer"
              onClick={() => trackCTA('signup', 'demo_cta')}
              className="btn-primary"
            >
              Create account
            </a>
            <a
              href="/contact"
              onClick={() => trackCTA('book_call', 'demo_cta')}
              className="btn-secondary flex items-center gap-2"
            >
              <CalendarDays size={16} aria-hidden />
              Book a walkthrough
            </a>
          </div>
        </div>
      </div>
    </div>
  </>
);

export default Demo;
